import {
  type CompanyTexts,
  KEYED_DP_SEPARATOR,
  ProcessingStatus,
  type Question,
  type RatingDatapoints,
  RatingFileCategory,
  RatingType,
  type SaftDatapoints,
  UploadScopeEnum,
} from "@esgt/types"
import dayjs from "dayjs"
import { produce } from "immer"
import { type BigNumber, bignumber, evaluate, add as mathAdd, reviver } from "mathjs"
import { AccountingPeriod, AccountingPeriodRange } from "./AccountingPeriod"
import { transformDatapointInput } from "./inputProcessing/transformDatapointInput"
import { calculateOmpostering } from "./ompostering"
import type { RatingCommit, RatingEvent, RatingState } from "./types"

/**
 * Compute auto-sum scalar datapoints from keyed (`prefix_*`) datapoints.
 *
 * Creates one sum for every prefix-of-segments combination, using `_autosum`
 * as a reserved suffix segment. For a datapoint `hazWasteRecycled_kg_batteries`
 * (prefix = "hazWasteRecycled"):
 *   - `hazWasteRecycled_autosum`    = sum of ALL hazWasteRecycled_* values
 *   - `hazWasteRecycled_kg_autosum` = sum of all hazWasteRecycled_kg_* values
 *
 * Leaf-level sums (which would equal the single keyed value) are skipped.
 *
 * Because auto-sum names share the `{prefix}_` namespace, the existing
 * stale-clearing logic (which deletes all `{prefix}_*` keys) handles
 * cleanup automatically — no separate clear step is needed.
 */
function computeAutoSums(datapoints: Record<string, BigNumber>, prefix: string) {
  const fullPrefix = `${prefix}${KEYED_DP_SEPARATOR}`
  const entries = Object.entries(datapoints).filter(
    ([key]) => key.startsWith(fullPrefix) && !key.endsWith(`${KEYED_DP_SEPARATOR}autosum`),
  )
  if (entries.length === 0) return

  // Determine the maximum segment depth across all keyed datapoints
  let maxSegments = 0
  const parsed: Array<{ segments: string[]; val: BigNumber }> = []
  for (const [key, val] of entries) {
    const segments = key.slice(fullPrefix.length).split(KEYED_DP_SEPARATOR)
    if (segments.length > maxSegments) maxSegments = segments.length
    parsed.push({ segments, val })
  }

  // For each depth level (0 = total, 1 = first segment, …, maxSegments-1 = all-but-last)
  // we skip the leaf level (depth=maxSegments) because that sum equals the single value itself.
  for (let depth = 0; depth < maxSegments; depth++) {
    const groups = new Map<string, BigNumber>()

    for (const { segments, val } of parsed) {
      const groupKey = segments.slice(0, depth).join(KEYED_DP_SEPARATOR)
      const current = groups.get(groupKey) ?? bignumber(0)
      groups.set(groupKey, mathAdd(current, val) as BigNumber)
    }

    for (const [groupKey, sum] of groups) {
      const dpName = groupKey
        ? `${prefix}${KEYED_DP_SEPARATOR}${groupKey}${KEYED_DP_SEPARATOR}autosum`
        : `${prefix}${KEYED_DP_SEPARATOR}autosum`
      datapoints[dpName] = sum
    }
  }
}

/**
 * Applies a question's answer to the datapoints map.
 * Shared by AnswerValuesChanged (reads values from the event) and
 * DependentQuestionsEnabled (reads values from preserved answerValues).
 */
function applyAnswerToDatapoints(
  datapoints: Record<string, BigNumber>,
  question: Question,
  primaryAnswer: "true" | "false",
  values: Record<string, string | null>,
) {
  // Apply the datapoint formula (e.g. certScore = primaryValue)
  if (question.datapoint) {
    const primaryAnswerValues = question.datapoint.primaryAnswerValues as { yes: number; no: number }
    const scope = {
      primaryValue: primaryAnswerValues[primaryAnswer === "true" ? "yes" : "no"],
    }
    const val = evaluate(question.datapoint.formula, scope)
    if (typeof val === "number") {
      datapoints[question.datapoint.name] = bignumber(val)
    }
  }

  if (primaryAnswer === "true") {
    const textInputs = question.textInputs || []
    const inputsPerGroup = textInputs.length

    // Clear prefixed datapoints before re-applying so stale keys don't linger.
    // When the question uses staticKeys, only clear keys owned by this question
    // (matching the static key values). This allows multiple questions to share
    // the same linkedDatapointPrefix without interfering with each other.
    const ownedSuffixes =
      question.repeatableSource && "staticKeys" in question.repeatableSource
        ? new Set(question.repeatableSource.staticKeys.map((sk) => sk.key))
        : undefined

    for (const textInput of textInputs) {
      if (textInput.linkedDatapointPrefix !== undefined) {
        const prefix = `${textInput.linkedDatapointPrefix}${KEYED_DP_SEPARATOR}`
        for (const key of Object.keys(datapoints)) {
          if (!key.startsWith(prefix)) continue
          if (ownedSuffixes) {
            const suffix = key.slice(prefix.length)
            if (ownedSuffixes.has(suffix) || suffix === "autosum") {
              delete datapoints[key]
            }
          } else {
            delete datapoints[key]
          }
        }
      }
    }

    // For repeatable inputs, process all input_N slots present in the
    // event. Otherwise process one slot per textInput definition.
    const totalSlots = question.repeatableInputs
      ? Object.keys(values)
          .filter((k) => k.startsWith("input_"))
          .reduce((max, k) => Math.max(max, Number.parseInt(k.split("_")[1], 10) + 1), 0)
      : inputsPerGroup

    // go through text inputs in question and update datapoints if they define a value
    for (let idx = 0; idx < totalSlots; idx++) {
      const textInput = textInputs[idx % inputsPerGroup]
      if (!textInput) continue
      const value = (values[`input_${idx}`] ?? undefined) as string

      if (textInput.linkedDatapoint) {
        const datapointValue = transformDatapointInput(value, textInput)
        if (datapointValue === undefined) {
          delete datapoints[textInput.linkedDatapoint]
        } else if (datapointValue >= 0) {
          datapoints[textInput.linkedDatapoint] = bignumber(datapointValue)
        }
      }

      // Handle keyed/prefixed datapoints (e.g. "årsverkLand_SE")
      //
      // Note: free-text input values may contain characters (spaces, +, -, etc.)
      // that are not valid in MathJS identifiers. This means keyed datapoints
      // whose suffix includes user-provided text (e.g. "materialeFlowBrukt_kg_Steel beam")
      // cannot be referenced directly in KPI formulas. Use autoSum-generated
      // datapoints instead (e.g. "materialeFlowBrukt_kg_autosum"), which only
      // contain system-controlled segments and are safe for formula use.
      if (textInput.linkedDatapointPrefix !== undefined && textInput.keysFromInput !== undefined) {
        const groupStart = Math.floor(idx / inputsPerGroup) * inputsPerGroup
        // Replace KEYED_DP_SEPARATOR in free-text values so user input does not
        // introduce extra segments that break suffix parsing and autoSum grouping.
        const keyParts = textInput.keysFromInput.map((ki) => {
          const v = values[`input_${groupStart + ki}`]
          return typeof v === "string" ? v.replaceAll(KEYED_DP_SEPARATOR, " ") : v
        })
        const keyValue = keyParts.every((p) => p) ? keyParts.join(KEYED_DP_SEPARATOR) : undefined
        const datapointValue = transformDatapointInput(value, textInput)
        if (keyValue && datapointValue !== undefined && datapointValue >= 0) {
          const datapointKey = `${textInput.linkedDatapointPrefix}${KEYED_DP_SEPARATOR}${keyValue}`
          datapoints[datapointKey] = bignumber(datapointValue)
        }
      }
    }

    // Auto-sum: compute aggregate scalar datapoints from keyed datapoints.
    for (const textInput of textInputs) {
      if (textInput.autoSum && textInput.linkedDatapointPrefix) {
        computeAutoSums(datapoints, textInput.linkedDatapointPrefix)
      }
    }
  }

  if (primaryAnswer === "false") {
    for (const textInput of question.textInputs || []) {
      if (textInput.linkedDatapoint) {
        if (textInput.valueWhenAnswerNo !== undefined) {
          datapoints[textInput.linkedDatapoint] = bignumber(textInput.valueWhenAnswerNo)
        } else {
          delete datapoints[textInput.linkedDatapoint]
        }
      }

      // Clean up keyed/prefixed datapoints when the answer is "no"
      if (textInput.linkedDatapointPrefix !== undefined) {
        const prefix = `${textInput.linkedDatapointPrefix}${KEYED_DP_SEPARATOR}`
        for (const key of Object.keys(datapoints)) {
          if (key.startsWith(prefix)) {
            delete datapoints[key]
          }
        }
      }
    }
  }
}

export const ratingStateReducer = (
  currentState: RatingState | undefined,
  commit: RatingCommit,
  event: RatingEvent,
): RatingState => {
  if (!currentState) {
    if (event.type === "RatingCreated") {
      const { type: _, ...eventParams } = event
      return {
        phase: "SUBMISSION",
        datapoints: {} as SaftDatapoints,
        approvedKpis: {},
        overriddenKpisById: {},
        ...eventParams,
        genericFiles: {},
        uploads: [],
        answerValues: {},
        additionalQuestions: [],
        anomalies: [],
        earlierAnomalies: [],
        ratingComment: "",
        ratingType: eventParams.ratingType || RatingType.Full,
        dataFilesProcessingStatus: {},
        companyTexts: {},
        reportHistory: [],
      }
    }
    throw new Error(`Missing state when we encountered ${event.type}`)
  }

  switch (event.type) {
    case "BrregInformationRetrieved": {
      return produce(currentState, (ns) => {
        ns.brreg = event.data
        const periodEnd = dayjs().subtract(1, "month").endOf("month")
        const firstAvailable = dayjs(event.data.stiftelsesdato).add(1, "month").startOf("month")
        const fullYear = dayjs(periodEnd).subtract(11, "months").startOf("month")

        const periodStart = firstAvailable > fullYear ? firstAvailable : fullYear

        ns.period = new AccountingPeriodRange(new AccountingPeriod(periodStart), new AccountingPeriod(periodEnd))
      })
    }

    case "DatapointsSet": {
      return produce(currentState, (ns) => {
        for (const key of Object.keys(event.datapoints) as Array<keyof RatingDatapoints>) {
          const val = event.datapoints[key]
          if (val === null || val === "") {
            delete ns.datapoints[key]
          }
          // if an event datapoint value is undefined, don't overwrite value
          else if (val !== undefined) {
            ns.datapoints[key] = bignumber(val)
          }
        }
      })
    }

    case "KpiValueApproved": {
      return produce(currentState, (ns) => {
        ns.approvedKpis[event.kpiId] = event.approved
      })
    }

    case "KpiValueOverridden": {
      return produce(currentState, (ns) => {
        ns.overriddenKpisById[event.kpiId] = { value: event.value, reason: event.reason }
      })
    }

    case "KpiValueOverrideRemoved": {
      return produce(currentState, (ns) => {
        delete ns.overriddenKpisById[event.kpiId]
      })
    }

    case "Ompostering": {
      return produce(currentState, (ns) => {
        /*
					Config for which omposterings that can be done is set in core/src/models/CompanyRating/dataFileProcessing/SAFT.ts
					Ompostering is always done to a datapoint that can be negative/positive and vice/verca is added/subtracted in a from-datapoint if set
					Both the amount and the datapoint can be positive/negative, and calculateOmpostering handles all of the options that can occur.

					NOTE:
					If this needs to be changed, do it with care as this is set ut to be in correct according to norwegian accounting-standards
				*/

        ns.datapoints[event.omposteringsRule.to] = calculateOmpostering(
          ns.datapoints[event.omposteringsRule.to] as BigNumber,
          bignumber(event.amount),
        )

        if (event.omposteringsRule.from) {
          ns.datapoints[event.omposteringsRule.from] = calculateOmpostering(
            ns.datapoints[event.omposteringsRule.from] as BigNumber,
            bignumber(event.amount),
          )
        }

        if (event.omposteringsRule.affectedDatapoints.length > 0) {
          for (const affectedDatapoints of event.omposteringsRule.affectedDatapoints) {
            ns.datapoints[affectedDatapoints] = calculateOmpostering(
              ns.datapoints[affectedDatapoints] as BigNumber,
              bignumber(event.amount),
            )
          }
        }
        if (event.anomalyId) {
          const anomaly = ns.anomalies[Number.parseInt(event.anomalyId, 10)]
          anomaly.ok = true
          anomaly.comment = `Omklassifisert til ${event.omposteringsRule.to}`
        }
      })
    }

    case "FileUploaded": {
      return produce(currentState, (ns) => {
        const { type: _, ...props } = event
        ns.uploads.push({ ...props, processed: !props.needsProcessing, valid: !props.needsProcessing })
        if (event.ratingFileType === RatingFileCategory.a07) {
          ns.dataFilesProcessingStatus.a07 = undefined
        } else if (event.ratingFileType === RatingFileCategory.saft) {
          ns.dataFilesProcessingStatus.saft = undefined
        }
      })
    }

    case "FileProcessed": {
      return produce(currentState, (ns) => {
        const uploadIndex = [...ns.uploads].findIndex((e) => e.uploadId === event.uploadId)
        ns.uploads[uploadIndex] = {
          ...ns.uploads[uploadIndex],
          parsedData: event.parsedData,
          processed: true,
          valid: !event.errorMsg,
          processFailureMessages: event.errorMsg,
          processWarningMessages: event.warningMsg,
        }
      })
    }

    case "UploadArchived": {
      return produce(currentState, (ns) => {
        const upload = ns.uploads.find((upload) => upload.uploadId === event.uploadId)
        if (upload?.scope === UploadScopeEnum.A07) {
          ns.dataFilesProcessingStatus.a07 = undefined
        } else if (upload?.scope === UploadScopeEnum.SAFT) {
          ns.dataFilesProcessingStatus.saft = undefined
        }
        ns.uploads = ns.uploads.filter((upload) => upload.uploadId !== event.uploadId)
      })
    }

    case "UploadsArchived": {
      const removeUploadIds = new Set(event.uploadIds)
      return produce(currentState, (ns) => {
        if (ns.uploads.some((upload) => removeUploadIds.has(upload.uploadId) && upload.scope === UploadScopeEnum.A07)) {
          ns.dataFilesProcessingStatus.a07 = undefined
        } else if (
          ns.uploads.some((upload) => removeUploadIds.has(upload.uploadId) && upload.scope === UploadScopeEnum.SAFT)
        ) {
          ns.dataFilesProcessingStatus.saft = undefined
        }
        ns.uploads = ns.uploads.filter((upload) => !removeUploadIds.has(upload.uploadId))
      })
    }

    case "AnswerValuesRemoved": {
      return produce(currentState, (ns) => {
        for (const scope of event.scopes) {
          // Remove any input values except Yes/No
          if (ns.answerValues[scope]) {
            ns.answerValues[scope] = { primaryAnswer: ns.answerValues[scope].primaryAnswer }
          }
        }
      })
    }

    case "AnswerValuesChanged": {
      return produce(currentState, (ns) => {
        // Note that this can be undefined: yes/no question about A07 also uses this event
        const question = event.question
        const primaryAnswer = event.values?.primaryAnswer as "true" | "false" | undefined

        if (question && (primaryAnswer || question.noPrimaryValue)) {
          applyAnswerToDatapoints(ns.datapoints, question, primaryAnswer ?? "true", event.values)

          // Legacy: handle dependentQuestions carried on the AnswerValuesChanged event
          // (backward compat for events emitted before DependentQuestionsDisabled/Enabled existed).
          // The old command always included dependentQuestions on the parent's own event,
          // so both "true" and "false" branches are needed for correct replay of historical events.
          if (primaryAnswer === "true") {
            for (const subQuestion of event.dependentQuestions || []) {
              for (const textInput of subQuestion.textInputs || []) {
                if (textInput.linkedDatapoint && textInput.valueWhenDisabled !== undefined) {
                  delete ns.datapoints[textInput.linkedDatapoint]
                }
              }
            }
          }

          if (primaryAnswer === "false") {
            for (const subQuestion of event.dependentQuestions || []) {
              for (const textInput of subQuestion.textInputs || []) {
                if (textInput.linkedDatapoint && textInput.valueWhenDisabled !== undefined) {
                  ns.datapoints[textInput.linkedDatapoint] = bignumber(textInput.valueWhenDisabled)
                }
              }
            }
          }
        }

        ns.answerValues[event.scope] = { ...ns.answerValues[event.scope], ...event.values }
      })
    }

    case "DependentQuestionsDisabled": {
      return produce(currentState, (ns) => {
        for (const question of event.questions) {
          // Delete the datapoint when a question is disabled by its parent.
          if (question.datapoint) {
            delete ns.datapoints[question.datapoint.name]
          }

          // Zero out datapoints: prefer valueWhenDisabled (the parent-disabled semantic),
          // fall back to valueWhenAnswerNo (questions like 3.1.1 only define this one),
          // otherwise delete the datapoint.
          for (const textInput of question.textInputs || []) {
            if (textInput.linkedDatapoint) {
              const disabledValue = textInput.valueWhenDisabled ?? textInput.valueWhenAnswerNo
              if (disabledValue !== undefined) {
                ns.datapoints[textInput.linkedDatapoint] = bignumber(disabledValue)
              } else {
                delete ns.datapoints[textInput.linkedDatapoint]
              }
            }

            // Clean up keyed/prefixed datapoints. When the question uses staticKeys,
            // only delete keys owned by this question to avoid clearing datapoints
            // from other questions that share the same prefix.
            if (textInput.linkedDatapointPrefix !== undefined) {
              const ownedKeys =
                question.repeatableSource && "staticKeys" in question.repeatableSource
                  ? new Set(question.repeatableSource.staticKeys.map((sk) => sk.key))
                  : undefined

              const prefix = `${textInput.linkedDatapointPrefix}${KEYED_DP_SEPARATOR}`
              for (const key of Object.keys(ns.datapoints)) {
                if (!key.startsWith(prefix)) continue
                if (ownedKeys) {
                  const suffix = key.slice(prefix.length)
                  if (ownedKeys.has(suffix)) {
                    delete ns.datapoints[key]
                  }
                } else {
                  delete ns.datapoints[key]
                }
              }
            }
          }
        }

        // Recalculate auto-sums for any prefixes affected by the disabled questions.
        // Without this, the autosum would remain stale after keyed datapoints are removed.
        const autoSumPrefixes = new Set<string>()
        for (const question of event.questions) {
          for (const textInput of question.textInputs || []) {
            if (textInput.autoSum && textInput.linkedDatapointPrefix) {
              autoSumPrefixes.add(textInput.linkedDatapointPrefix)
            }
          }
        }
        for (const prefix of autoSumPrefixes) {
          // Delete the existing autosum so it can be recalculated from remaining entries
          const autosumKey = `${prefix}${KEYED_DP_SEPARATOR}autosum`
          delete ns.datapoints[autosumKey]
          computeAutoSums(ns.datapoints, prefix)
        }
      })
    }

    case "DependentQuestionsEnabled": {
      return produce(currentState, (ns) => {
        for (const question of event.questions) {
          const savedValues = ns.answerValues[question.id]
          if (!savedValues) continue

          applyAnswerToDatapoints(ns.datapoints, question, savedValues.primaryAnswer ?? "true", savedValues)
        }
      })
    }

    case "ContactChanged": {
      return produce(currentState, (ns) => {
        ns.contactEmail = event.email
        ns.contactName = event.name
        ns.contactPhone = event.phone
      })
    }

    case "CommentChanged": {
      return produce(currentState, (ns) => {
        ns.ratingComment = event.comment
      })
    }

    case "PeriodChanged": {
      return produce(currentState, (ns) => {
        ns.period = new AccountingPeriodRange(new AccountingPeriod(event.start), new AccountingPeriod(event.end))
        ns.phase = "SUBMISSION"
      })
    }

    case "RatingProfileChanged": {
      return produce(currentState, (ns) => {
        ns.ratingProfileId = event.ratingProfileId
        ns.ratingProfileVersion = event.ratingProfileVersion
          ? new Date(event.ratingProfileVersion).toISOString().replace("T", " ").replace("Z", "+00")
          : undefined
      })
    }

    case "DocumentationSubmitted": {
      return produce(currentState, (ns) => {
        ns.phase = "RATING_IN_PROGRESS"
        ns.lastSubmitted = new Date(commit.timestamp)
        ns.reopenedScopes = undefined
      })
    }

    case "RequestDocumentationProcessing": {
      return produce(currentState, (ns) => {
        ns.phase = "PROCESSING_DOCUMENTATION"
        ns.lastSubmitted = new Date(commit.timestamp)
        ns.dataFilesProcessingStatus = {}
      })
    }

    case "DocumentationProcessed": {
      return produce(currentState, (ns) => {
        ns.phase = "RATING_IN_PROGRESS"
        ns.reopenedScopes = undefined
      })
    }

    case "DocumentationProcessingFailed": {
      return produce(currentState, (ns) => {
        ns.phase = "RATING_IN_PROGRESS"
        ns.reopenedScopes = undefined
        const processingStatus = {
          status: ProcessingStatus.Failed,
          errorMsg: event.errorMsg,
        }
        if (event.ratingFileType === RatingFileCategory.a07) {
          ns.dataFilesProcessingStatus.a07 = processingStatus
        } else if (event.ratingFileType === RatingFileCategory.saft) {
          ns.dataFilesProcessingStatus.saft = processingStatus
        }
      })
    }

    case "RatingAborted": {
      return produce(currentState, (ns) => {
        ns.phase = "ABORTED"
      })
    }

    case "AdditionalQuestionAdded": {
      return produce(currentState, (ns) => {
        const { type: _, ...eventProps } = event

        ns.phase = "SUBMISSION"
        ns.reopenedScopes = [...(ns.reopenedScopes || []), "additionalQuestions"]
        ns.currentAdditionalQuestionNumber = Number.parseInt(eventProps.questionNumber.split(".")[1], 10)

        ns.additionalQuestions.push({
          ...eventProps,
          timestamp: new Date(commit.timestamp),
        })
      })
    }

    case "AdditionalQuestionRemoved": {
      return produce(currentState, (ns) => {
        ns.additionalQuestions = ns.additionalQuestions.filter((q) => q.id.toString() !== event.id)
      })
    }

    case "RatingCompleted": {
      return produce(currentState, (ns) => {
        ns.phase = "COMPLETED"
        // Lock rating to ratingProfileVersion (if not already locked)
        ns.ratingProfileVersion =
          currentState.ratingProfileVersion ||
          (event.ratingProfileVersion
            ? new Date(event.ratingProfileVersion).toISOString().replace("T", " ").replace("Z", "+00")
            : undefined)
        ns.pdfReportKey = event.pdfReportKey
        ns.completedAt = new Date(commit.timestamp)
        if (!ns.reportHistory) ns.reportHistory = []
        ns.reportHistory.push({
          pdfReportKey: event.pdfReportKey,
          createdAt: new Date(commit.timestamp),
        })
      })
    }

    case "ReportRegenerated": {
      return produce(currentState, (ns) => {
        ns.pdfReportKey = event.pdfReportKey
        if (!ns.reportHistory) ns.reportHistory = []
        ns.reportHistory.push({
          pdfReportKey: event.pdfReportKey,
          createdAt: new Date(commit.timestamp),
        })
      })
    }

    case "DocumentationAmendmentsRequested": {
      return produce(currentState, (ns) => {
        ns.phase = "SUBMISSION"
        ns.reopenedScopes = event.scopes

        if (ns.anomalies.length) {
          ns.earlierAnomalies.unshift(ns.anomalies)
        }

        ns.anomalies = []

        if (event.scopes) {
          for (const previousAnomaly of currentState.anomalies) {
            if (previousAnomaly.source === "SAFT" && !event.scopes.includes("finances")) {
              ns.anomalies.push(previousAnomaly)
            }
          }

          if (event.scopes.includes("altinn")) {
            ns.dataFilesProcessingStatus.a07 = undefined
          }
          if (event.scopes.includes("finances")) {
            ns.dataFilesProcessingStatus.saft = undefined
          }
        }
      })
    }

    case "RequestDatafilesProcessing": {
      return produce(currentState, (ns) => {
        ns.phase = "PROCESSING_DATAFILES"
      })
    }

    case "DatafilesProcessed": {
      return produce(currentState, (ns) => {
        ns.anomalies = [...ns.anomalies, ...JSON.parse(JSON.stringify(event.anomalies), reviver)]
        ns.datapoints = { ...ns.datapoints, ...JSON.parse(JSON.stringify(event.datapoints), reviver) }
      })
    }

    case "SelfServiceRequestDatafilesProcessing": {
      return produce(currentState, (ns) => {
        const processingStatus = {
          status: ProcessingStatus.Processing,
        }
        if (event.ratingFileType === RatingFileCategory.a07) {
          ns.dataFilesProcessingStatus.a07 = processingStatus
        } else if (event.ratingFileType === RatingFileCategory.saft) {
          ns.dataFilesProcessingStatus.saft = processingStatus
        }
      })
    }

    case "SelfServiceDatafilesProcessed": {
      return produce(currentState, (ns) => {
        // Determine the anomaly source tag for this file type so we can replace
        // rather than append. This prevents duplicate anomalies when a user
        // re-uploads (replaces) a file.
        const sourceTag =
          event.ratingFileType === RatingFileCategory.saft
            ? "SAFT"
            : event.ratingFileType === RatingFileCategory.a07
              ? "A07"
              : undefined

        const newAnomalies = JSON.parse(JSON.stringify(event.anomalies), reviver)
        ns.anomalies = [...ns.anomalies.filter((a) => !sourceTag || a.source !== sourceTag), ...newAnomalies]
        ns.datapoints = { ...ns.datapoints, ...JSON.parse(JSON.stringify(event.datapoints), reviver) }
        const processingStatus = {
          status: ProcessingStatus.Completed,
        }
        if (event.ratingFileType === RatingFileCategory.a07) {
          ns.dataFilesProcessingStatus.a07 = processingStatus
        } else if (event.ratingFileType === RatingFileCategory.saft) {
          ns.dataFilesProcessingStatus.saft = processingStatus
        }
      })
    }

    case "SelfServiceDatafilesProcessingFailed": {
      return produce(currentState, (ns) => {
        const processingStatus = {
          status: ProcessingStatus.Failed,
          errorMsg: event.errorMsg,
        }
        if (event.ratingFileType === RatingFileCategory.a07) {
          ns.dataFilesProcessingStatus.a07 = processingStatus
        } else if (event.ratingFileType === RatingFileCategory.saft) {
          ns.dataFilesProcessingStatus.saft = processingStatus
        }
      })
    }

    case "AnomalyApproved": {
      return produce(currentState, (ns) => {
        // replace with proper str id
        const anomaly = ns.anomalies[Number.parseInt(event.anomalyId, 10)]
        anomaly.ok = true
        anomaly.comment = event.comment
      })
    }

    case "AnomalyUnapproved": {
      return produce(currentState, (ns) => {
        // replace with proper str id
        const anomaly = ns.anomalies[Number.parseInt(event.anomalyId, 10)]
        anomaly.ok = false
        anomaly.comment = undefined
      })
    }

    case "RatingUpgradedToFull": {
      return produce(currentState, (ns) => {
        ns.ratingType = RatingType.Full
        ns.ratingUpgradedToFull = true
        ns.phase = "SUBMISSION"
      })
    }

    case "CompanyTextChanged": {
      return produce(currentState, (ns) => {
        ns.companyTexts[event.scope as CompanyTexts] = event.text
      })
    }

    default: {
      return currentState
    }
  }
}
